import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { syncAllCartGifts, markSwimsuitGiftDeclined } from '@theme/cart-gift-sync';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', (event) => this.#onBackdropClick(event));

    this.checkoutButton.addEventListener('click', (event) => this.#handleCheckoutButtonClick(event));

    document.addEventListener(ThemeEvents.cartUpdated, (event) => this.#handleCartUpdate(event));

    document.addEventListener(ThemeEvents.cartOpen, () => this.open());
    document.addEventListener(ThemeEvents.cartClose, () => this.close());
  }

  /**
   * Nettoie au chargement les lignes cadeau orphelines (cadeau présent sans
   * produit déclencheur) que le client ne peut pas supprimer manuellement.
   */
  async #reconcileGifts() {
    try {
      const sectionId = this.dataset.sectionId;
      const result = await syncAllCartGifts({ sectionId });
      if (result && !result.errors && !result.status && result.sections) {
        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: result.item_count,
              source: 'cart-drawer-reconcile',
              sections: result.sections,
            },
          ),
        );
      }
    } catch (e) {
      console.error(e);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, () => this.close());
    document.removeEventListener(ThemeEvents.cartOpen, () => this.open());

    document.removeEventListener(ThemeEvents.cartUpdated, (event) => this.#handleCartUpdate(event));

    this.checkoutButton.removeEventListener('click', (event) => this.#handleCheckoutButtonClick(event));

    this.removeEventListener('click', (event) => this.#onBackdropClick(event));
  }

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    this.#reconcileGifts();
  }

  close() {
    this.classList.add('is-closing');

    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125);
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  renderCartContents(parsedState) {
    const section_cart_drawer_id = this.dataset.sectionId;

    const currentCartDrawer = this.querySelector('.cart-drawer__inner');
    const newCartDrawer = new DOMParser().parseFromString(parsedState.sections[section_cart_drawer_id], 'text/html');

    currentCartDrawer.innerHTML = newCartDrawer.querySelector('.cart-drawer__inner').innerHTML;

    setTimeout(() => {
      if (parsedState.itemCount == 0) {
        this.classList.add('cart-drawer--empty');
      } else {
        this.classList.remove('cart-drawer--empty');
      }
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', (event) => this.#handleItemRemove(event));
    this.quantitySelectorInput?.addEventListener('change', (event) => this.#handleQuantityInputChange(event));
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', (event) => this.#handleQuantityInputChange(event));
    this.removeItemButton?.removeEventListener('click', (event) => this.#handleItemRemove(event));
  }

  #handleItemRemove(event) {
    event.preventDefault();
    const isGift = this.classList.contains('cart-drawer__item--gift-line');
    this.#updateLineItemQuantity(this.dataset.index, 0, { isGift });
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  async #updateLineItemQuantity(line, quantity, { isGift = false } = {}) {
    this.#enableLoading();
    const sectionId = this.closest('cart-drawer').dataset.sectionId;

    try {
      const body = JSON.stringify({
        line,
        quantity,
        sections: sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      let parsedResponseText = await response.json();

      if (parsedResponseText.errors) {
        const message = Array.isArray(parsedResponseText.errors) ? parsedResponseText.errors.join(', ') : parsedResponseText.errors;
        document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message } }));
        return;
      }

      if (isGift) {
        // Le client retire volontairement le cadeau : on mémorise le refus pour que
        // la synchro ne le réajoute pas, et on ne relance pas syncAllCartGifts.
        const declinedResponse = await markSwimsuitGiftDeclined({ sectionId });
        if (declinedResponse && !declinedResponse.errors && !declinedResponse.status) {
          parsedResponseText = declinedResponse;
        }
      } else {
        const syncedResponse = await syncAllCartGifts({ sectionId });
        if (syncedResponse && !syncedResponse.errors && !syncedResponse.status) {
          parsedResponseText = syncedResponse;
        }
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: parsedResponseText.item_count,
            source: 'cart-drawer-item',
            sections: parsedResponseText.sections,
          },
        ),
      );
    } catch (e) {
      console.error(e);
    } finally {
      this.#disableLoading();
    }
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
